import React, { useEffect, useRef, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import ActivityLoader from '../../components/Common/ActivityLoader';
import Notification from '../../components/Common/Notification';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import functions from '../../util/functions';
import printContent from '../../util/araknerdlib';
import { useCashFlow } from '../../DataStore/CashFlowContext';
import DateRangeSelector from '../../components/Report/DateRangeSelector';
import AlertDateSelector from '../../components/Report/AlertDateSelector';

const DailyCashFlowPage = () => {
    const statementRef = useRef();
    const [showBasicDateRange, setShowBasicDateRange] = useState(false);
    const [showAdvancedDateRange, setShowAdvancedDateRange] = useState(false);


    const {
        getStatement, 
        statement,
        dispatch, 
    } = useCashFlow();

    useEffect(() => {
        getStatement();
    }, []);

    const handleConfirmDateRange = async (range) => {
        dispatch({ type: 'dateRange', payload: range });
        await getStatement(true, range);
    };

    const handleConfirmSpecificDates = async (dates) => {
        await getStatement(true, null, dates);
    };

    const PageActions = () => (<>
        <ActionDropdownItem iconClass="fas fa-calendar-alt text-success" label="Filter Date Range" onClick={()=>setShowBasicDateRange(true)}/>
        <ActionDropdownItem iconClass="fas fa-calendar-alt text-info" label="Filter Specific Dates..." onClick={()=>setShowAdvancedDateRange(true)}/>
        <ActionDropdownItem iconClass="fa fa-print text-secondary" label="Print" onClick={()=>printContent(statementRef, "Cash Flow Statement")}/>
        <ActionDropdownItem iconClass="fa fa-refresh text-info" label="Refresh" onClick={() => getStatement(true)} />
    </>);

    return (
        <>
        <PageContainer>
            <MainContent>
                <PageTitle
                    title="Cash Flow Statement"
                    description="Track daily income and expenditure summary."
                    iconClass="fas fa-calendar-alt"
                    sticky={true}
                    PageActions={PageActions}
                />

                <div ref={statementRef}>

                    <div className="content-wrapper d-flex">
                        {/* Statement Header */}
                        <div className="header-section" style={{ flex: 3 }}>
                            <h4>Cash Flow Statement</h4>
                            <p>Summary of daily income & expenditure</p>

                            {functions.isObject(statement) && ( <p>
                                <strong>Financial Period: </strong>{' '}
                                {statement.opening_date && <>{functions.formatDate(statement.opening_date)} - {functions.formatDate(statement.closing_date)}</>}
                            </p>)}
                        </div>

                        {/* Statement Summary */}
                        {functions.isObject(statement) && statement.totals && (
                            <div className="summary-table" style={{ flex: 1 }}>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Total Revenue</th>
                                            <td>{functions.numberFormat(statement.totals.income)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total Expenditure</th>
                                            <td>{functions.numberFormat(statement.totals.expense)}</td>
                                        </tr>
                                        <tr>
                                            <th>Net Revenue</th>
                                            <td>{functions.numberFormat(statement.totals.profit)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>


                <div className="card mb-3">
                    <div className="card-body">
                        <div className="table-responsive">
                        {functions.isObject(statement) && (
                            <div className="mb-4">
                                
                                <table className="table table-hover table-striped table-bordered">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Income</th>
                                            <th>Expense</th>
                                            <th>Net Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {statement.items.map((item, key) => (
                                            <tr key={key}>
                                                <td>{key + 1}</td>
                                                <td>{functions.formatDate(item.transaction_date)}</td>
                                                <td>UGX {functions.numberFormat(item.income)}</td>
                                                <td>UGX {functions.numberFormat(item.expense)}</td>
                                                <td className={item.profit >= 0 ? 'text-dark' : 'text-danger font-weight-bold'}>
                                                  UGX {functions.numberFormat(item.profit)}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="table-secondary font-weight-bold">
                                            <th colSpan="2">Total</th>
                                            <th>UGX {functions.numberFormat(statement.totals.income)}</th>
                                            <th>UGX {functions.numberFormat(statement.totals.expense)}</th>
                                            <th>UGX {functions.numberFormat(statement.totals.profit)}</th>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    {!statement && <ActivityLoader />}
                    {statement === "404" && <Notification message="No cash flow records found." closable={false} />}
                    </div>
                    </div>
                </div>
            </MainContent>
        </PageContainer>
        <DateRangeSelector
            show={showAdvancedDateRange}
            onClose={() => setShowAdvancedDateRange(false)}
            onConfirm={handleConfirmSpecificDates}
        />
        <AlertDateSelector
            show={showBasicDateRange}
            onClose={()=>setShowBasicDateRange(false)}
            onConfirm={handleConfirmDateRange}
            />
        </>
    );
};

export default DailyCashFlowPage;
